import React, { useEffect, useState } from "react";
import CashflowScenarios from "@/pages/finance/CashflowScenarios";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { StatusBadge, EmptyState } from "@/components/app/ui-bits";
import {
  Plus, Buildings, Bank, Calendar, Warning,
  CloudArrowUp, ArrowsClockwise, FileText, DownloadSimple,
  Lock, CheckCircle, Eye, Check, Sparkle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import AiExtractDialog from "@/components/app/AiExtractDialog";
import ExportMenu from "@/components/app/ExportMenu";

// ══════════════════════════════════════════════════════════════
// Section 7 — Asset Register (Finance view)
// ══════════════════════════════════════════════════════════════
export function AssetRegisterTab({ canEdit }) {
  const [data, setData] = useState({ items: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/finance/assets-register");
      setData(d);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const runDep = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/finance/assets-register/run-depreciation");
      toast.success(`Depreciation run · ${r.affected} asset(s) · ${r.period}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="dc-card p-4 border-l-4 border-l-[#1E5CBA]"><div className="overline">Total cost</div><div className="mt-1 font-display text-2xl font-bold tabular-nums text-slate-900">{formatCurrency(data.totals.cost || 0)}</div></div>
        <div className="dc-card p-4 border-l-4 border-l-amber-500"><div className="overline">Accumulated depreciation</div><div className="mt-1 font-display text-2xl font-bold tabular-nums text-amber-700">{formatCurrency(data.totals.accumulated_depreciation || 0)}</div></div>
        <div className="dc-card p-4 border-l-4 border-l-emerald-500"><div className="overline">Net book value</div><div className="mt-1 font-display text-2xl font-bold tabular-nums text-emerald-700">{formatCurrency(data.totals.nbv || 0)}</div></div>
      </div>

      <div className="dc-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="font-display text-[15px] font-semibold text-slate-900">Fixed Asset Register</h3>
          <div className="flex flex-wrap items-center gap-2">
            <ExportMenu module="finance-assets" />
            {canEdit && <Button size="sm" onClick={runDep} disabled={busy} data-testid="asset-run-dep-btn"><ArrowsClockwise size={12} className={`mr-1 ${busy ? "animate-spin" : ""}`} /> Run monthly depreciation</Button>}
          </div>
        </div>
        {loading ? <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
         : data.items.length === 0 ? <EmptyState icon={Buildings} title="No assets" description="Fixed assets will appear once seeded." />
         : (
          <div className="responsive-table-wrap">
            <table className="data-table w-full min-w-[1100px]">
              <thead>
                <tr><th>Asset ID</th><th>Description</th><th>Category</th><th className="text-right">Cost</th><th className="text-right">Accum dep</th><th className="text-right">NBV</th><th>Location</th><th className="text-right">Rate %</th><th>Last verified</th></tr>
              </thead>
              <tbody>
                {data.items.map((a) => (
                  <tr key={a.id} data-testid={`asset-row-${a.asset_id}`}>
                    <td className="px-5 py-3 font-mono-alt text-xs">{a.asset_id}</td>
                    <td className="px-5 py-3 font-semibold text-slate-900">{a.description}</td>
                    <td className="px-5 py-3 text-xs text-slate-600">{a.category}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{formatCurrency(a.cost)}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums text-amber-700">{formatCurrency(a.accumulated_depreciation)}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums font-semibold text-emerald-700">{formatCurrency(a.nbv)}</td>
                    <td className="px-5 py-3 text-xs">{a.location}</td>
                    <td className="px-5 py-3 text-right font-mono-alt text-xs tabular-nums">{a.depreciation_rate_pct}%</td>
                    <td className="px-5 py-3 text-xs">{formatDate(a.last_verified)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Section 8 — Cashflow Forecast
// ══════════════════════════════════════════════════════════════
const SCENARIOS = {
  base: { label: "Base case", factor: 1 },
  conservative: { label: "Conservative −20%", factor: 0.8 },
  optimistic: { label: "Optimistic +20%", factor: 1.2 },
};

function CashflowTabBase({ canEdit }) {
  const [data, setData] = useState(null);
  const [scenario, setScenario] = useState("base");
  const [savingPeriod, setSavingPeriod] = useState(null);

  const load = async () => {
    const { data: d } = await api.get("/finance/cashflow");
    setData(d);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const updateCell = (period, kind, key, value) => {
    setData((prev) => {
      const next = { ...prev, rows: prev.rows.map((r) => r.period === period ? { ...r, [kind]: { ...r[kind], [key]: parseFloat(value || 0) } } : r) };
      return next;
    });
  };

  const updateTax = (period, value) => {
    setData((prev) => ({ ...prev, rows: prev.rows.map((r) => r.period === period ? { ...r, tax_outflow: parseFloat(value || 0) } : r) }));
  };

  const saveRow = async (row) => {
    setSavingPeriod(row.period);
    try {
      await api.post("/finance/cashflow", row);
      toast.success(`Saved ${row.period}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSavingPeriod(null); }
  };

  if (!data) return <div className="font-mono-alt text-xs uppercase tracking-[0.25em] text-zinc-500">Loading…</div>;

  const factor = SCENARIOS[scenario].factor;
  const inflowKeys = Array.from(new Set(data.rows.flatMap((r) => Object.keys(r.inflows))));
  const outflowKeys = Array.from(new Set(data.rows.flatMap((r) => Object.keys(r.outflows))));

  const totalFor = (row) => {
    const inflow = Object.values(row.inflows || {}).reduce((a, b) => a + (b || 0), 0) * factor;
    const outflow = Object.values(row.outflows || {}).reduce((a, b) => a + (b || 0), 0) + (row.tax_outflow || 0);
    return { inflow, outflow, net: inflow - outflow };
  };

  let running = data.opening_balance;
  const closing = data.rows.map((r) => { running += totalFor(r).net; return running; });

  const totalsCol = data.rows.reduce((acc, r) => {
    const t = totalFor(r);
    acc.inflow += t.inflow;
    acc.outflow += t.outflow;
    return acc;
  }, { inflow: 0, outflow: 0 });

  return (
    <div className="space-y-5">
      <div className="dc-card p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="overline">Opening balance · all banks + petty cash</div>
          <div className="mt-1 font-display text-2xl font-bold tabular-nums text-slate-900">{formatCurrency(data.opening_balance)}</div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500">Scenario</Label>
          <Select value={scenario} onValueChange={setScenario}>
            <SelectTrigger className="w-56" data-testid="cashflow-scenario-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(SCENARIOS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="dc-card overflow-hidden">
        <div className="responsive-table-wrap">
          <table className="data-table w-full min-w-[860px]">
            <thead>
              <tr>
                <th>Line</th>
                {data.rows.map((r) => <th key={r.period} className="text-right">{r.period}</th>)}
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50/70"><td colSpan={5} className="px-5 py-2 overline">Inflows</td></tr>
              {inflowKeys.map((k) => (
                <tr key={`in-${k}`}>
                  <td className="px-5 py-2 text-sm">{k}</td>
                  {data.rows.map((r) => (
                    <td key={r.period + k} className="px-5 py-2 text-right">
                      {canEdit ? (
                        <Input type="number" className="h-8 text-right text-xs bg-yellow-50/60 border-yellow-200 text-blue-700" value={r.inflows[k] ?? 0} onChange={(e) => updateCell(r.period, "inflows", k, e.target.value)} data-testid={`cf-in-${r.period}-${k}`} />
                      ) : <span className="font-mono-alt tabular-nums">{formatCurrency(r.inflows[k] || 0)}</span>}
                    </td>
                  ))}
                  <td className="px-5 py-2 text-right font-mono-alt tabular-nums">{formatCurrency(data.rows.reduce((a, r) => a + ((r.inflows[k] || 0) * factor), 0))}</td>
                </tr>
              ))}
              <tr className="font-semibold"><td className="px-5 py-2 text-sm">Total inflows</td>
                {data.rows.map((r) => <td key={`tin-${r.period}`} className="px-5 py-2 text-right font-mono-alt tabular-nums text-emerald-700">{formatCurrency(totalFor(r).inflow)}</td>)}
                <td className="px-5 py-2 text-right font-mono-alt tabular-nums text-emerald-700">{formatCurrency(totalsCol.inflow)}</td>
              </tr>

              <tr className="bg-slate-50/70"><td colSpan={5} className="px-5 py-2 overline">Outflows</td></tr>
              {outflowKeys.map((k) => (
                <tr key={`out-${k}`}>
                  <td className="px-5 py-2 text-sm">{k}</td>
                  {data.rows.map((r) => (
                    <td key={r.period + k} className="px-5 py-2 text-right">
                      {canEdit ? (
                        <Input type="number" className="h-8 text-right text-xs bg-yellow-50/60 border-yellow-200 text-blue-700" value={r.outflows[k] ?? 0} onChange={(e) => updateCell(r.period, "outflows", k, e.target.value)} data-testid={`cf-out-${r.period}-${k}`} />
                      ) : <span className="font-mono-alt tabular-nums">{formatCurrency(r.outflows[k] || 0)}</span>}
                    </td>
                  ))}
                  <td className="px-5 py-2 text-right font-mono-alt tabular-nums">{formatCurrency(data.rows.reduce((a, r) => a + (r.outflows[k] || 0), 0))}</td>
                </tr>
              ))}
              <tr><td className="px-5 py-2 text-sm">Tax liability</td>
                {data.rows.map((r) => (
                  <td key={`tax-${r.period}`} className="px-5 py-2 text-right">
                    {canEdit ? <Input type="number" className="h-8 text-right text-xs bg-yellow-50/60 border-yellow-200 text-blue-700" value={r.tax_outflow ?? 0} onChange={(e) => updateTax(r.period, e.target.value)} /> : formatCurrency(r.tax_outflow || 0)}
                  </td>
                ))}
                <td className="px-5 py-2 text-right font-mono-alt tabular-nums">{formatCurrency(data.rows.reduce((a, r) => a + (r.tax_outflow || 0), 0))}</td>
              </tr>
              <tr className="font-semibold"><td className="px-5 py-2 text-sm">Total outflows</td>
                {data.rows.map((r) => <td key={`tout-${r.period}`} className="px-5 py-2 text-right font-mono-alt tabular-nums text-red-700">{formatCurrency(totalFor(r).outflow)}</td>)}
                <td className="px-5 py-2 text-right font-mono-alt tabular-nums text-red-700">{formatCurrency(totalsCol.outflow)}</td>
              </tr>

              <tr className="border-t-2 border-slate-200">
                <td className="px-5 py-2 text-sm font-semibold">Net cashflow</td>
                {data.rows.map((r) => { const t = totalFor(r); return <td key={`net-${r.period}`} className={`px-5 py-2 text-right font-mono-alt tabular-nums ${t.net >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatCurrency(t.net)}</td>; })}
                <td className="px-5 py-2 text-right font-mono-alt tabular-nums">{formatCurrency(totalsCol.inflow - totalsCol.outflow)}</td>
              </tr>
              <tr>
                <td className="px-5 py-2 text-sm font-semibold">Closing balance</td>
                {closing.map((c, i) => <td key={`close-${i}`} className="px-5 py-2 text-right font-mono-alt tabular-nums font-bold text-slate-900" data-testid={`cf-close-${data.rows[i].period}`}>{formatCurrency(c)}</td>)}
                <td className="px-5 py-2 text-right">—</td>
              </tr>
            </tbody>
          </table>
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
            {data.rows.map((r) => (
              <Button key={`save-${r.period}`} size="sm" variant="outline" onClick={() => saveRow(r)} disabled={savingPeriod === r.period} data-testid={`cf-save-${r.period}`}>
                {savingPeriod === r.period ? "Saving…" : `Save ${r.period}`}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Section 9 — Bank Reconciliation
// ══════════════════════════════════════════════════════════════
const BANK_OPTIONS = [
  { k: "premier",    label: "Premier Bank · 020300078001" },
  { k: "amal",       label: "Amal Bank · 412102407904" },
  { k: "salaam",     label: "Salaam Bank · 31027047" },
  { k: "dahabshiil", label: "Dahabshiil Bank · 30100793490" },
];

const RECON_STATUS_LABEL = {
  in_progress: { label: "In progress", color: "amber" },
  reconciled: { label: "Reconciled (zero diff)", color: "emerald" },
  awaiting_approval: { label: "Awaiting CEO", color: "blue" },
  approved: { label: "Approved · Locked", color: "emerald" },
};

export function BankReconTab({ canEdit, isCeo }) {
  const today = new Date();
  const defaultPeriod = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [bankKey, setBankKey] = useState("premier");
  const [period, setPeriod] = useState(defaultPeriod);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState(null);

  const load = async () => {
    const { data } = await api.get("/finance/bank-recon", { params: { bank_key: bankKey } });
    setHistory(data.items || []);
    const exact = (data.items || []).find((x) => x.period === period);
    setForm(exact || {
      bank_key: bankKey, period,
      statement_balance: 0, deposits_in_transit: 0, outstanding_cheques: 0,
      book_balance: 0, interest_income: 0, bank_charges: 0, nsf_items: 0, notes: "",
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bankKey, period]);

  const adj_bank = (form?.statement_balance || 0) + (form?.deposits_in_transit || 0) - (form?.outstanding_cheques || 0);
  const adj_book = (form?.book_balance || 0) + (form?.interest_income || 0) - (form?.bank_charges || 0) - (form?.nsf_items || 0);
  const diff = +(adj_bank - adj_book).toFixed(2);
  const reconciled = Math.abs(diff) <= 0.01;
  const locked = form?.locked || form?.status === "approved";

  const save = async () => {
    try { await api.post("/finance/bank-recon", form); toast.success("Saved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async () => {
    try { await api.post(`/finance/bank-recon/${form.id}/submit-approval`); toast.success("Submitted to CEO"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const approve = async () => {
    try { await api.post(`/finance/bank-recon/${form.id}/approve`); toast.success("Reconciliation approved & locked"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const Section = ({ title, children, accent }) => (
    <div className="dc-card overflow-hidden">
      <div className={`border-b border-slate-100 px-5 py-2 overline ${accent || ""}`}>{title}</div>
      <div className="p-5 space-y-3">{children}</div>
    </div>
  );

  const Row = ({ label, k, sign }) => (
    <div className="grid grid-cols-2 items-center gap-2">
      <Label className="text-sm text-slate-700">{sign && <span className={`mr-1 font-mono-alt ${sign === "+" ? "text-emerald-700" : "text-red-700"}`}>{sign}</span>}{label}</Label>
      <Input type="number" step="0.01" className="text-right tabular-nums" value={form?.[k] ?? 0}
        onChange={(e) => setForm({ ...form, [k]: parseFloat(e.target.value || 0) })}
        disabled={locked || !canEdit} data-testid={`recon-${k}`} />
    </div>
  );

  if (!form) return <div className="font-mono-alt text-xs uppercase tracking-[0.25em] text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="dc-card p-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div>
          <Label>Bank</Label>
          <Select value={bankKey} onValueChange={setBankKey}>
            <SelectTrigger data-testid="recon-bank-select"><SelectValue /></SelectTrigger>
            <SelectContent>{BANK_OPTIONS.map((b) => <SelectItem key={b.k} value={b.k}>{b.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Period</Label>
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="recon-period" />
        </div>
        <div className="flex items-end justify-end">
          <StatusBadge status={form.status === "approved" ? "approved" : form.status === "awaiting_approval" ? "pending_review" : reconciled ? "active" : "expiring_soon"} />
        </div>
      </div>

      {canEdit && !locked && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed border-slate-300 bg-slate-50/60 px-4 py-2">
          <div className="text-xs text-slate-600">
            <Sparkle size={12} className="mr-1 inline-block text-[#1E5CBA]" weight="fill" />
            Tip: upload the bank statement PDF and AI will prefill statement balance, interest, charges & NSF for this bank & period.
          </div>
          <AiExtractDialog
            module="bank_statement"
            saveLabel="Apply to reconciliation"
            onCustomSave={async (clean) => {
              // Snap into the recon form, switching bank/period when the AI detected them.
              const detectedBank = (clean.bank_key || "").toLowerCase();
              if (detectedBank && BANK_OPTIONS.find((b) => b.k === detectedBank)) {
                setBankKey(detectedBank);
              }
              if (clean.period) {
                setPeriod(clean.period);
              }
              setForm((f) => ({
                ...(f || {}),
                bank_key: detectedBank || f?.bank_key,
                period: clean.period || f?.period,
                statement_balance: parseFloat(clean.statement_balance || 0) || 0,
                deposits_in_transit: parseFloat(clean.deposits_in_transit || 0) || 0,
                outstanding_cheques: parseFloat(clean.outstanding_cheques || 0) || 0,
                interest_income: parseFloat(clean.interest_income || 0) || 0,
                bank_charges: parseFloat(clean.bank_charges || 0) || 0,
                nsf_items: parseFloat(clean.nsf_items || 0) || 0,
                notes: clean.notes || f?.notes || "",
              }));
              toast.success("Statement values applied — review and Save when ready.");
              return { id: "applied" };
            }}
            trigger={
              <Button size="sm" variant="outline" data-testid="recon-ai-extract-btn">
                <Sparkle size={12} className="mr-1" /> Upload statement (AI Extract)
              </Button>
            }
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Section title="A · Bank Statement Balance">
          <Row label="Closing balance per statement" k="statement_balance" />
          <Row label="Deposits in transit" k="deposits_in_transit" sign="+" />
          <Row label="Outstanding cheques" k="outstanding_cheques" sign="−" />
          <div className="rounded-md bg-[#0f1e42] p-3 text-white">
            <div className="overline text-white/60">Adjusted bank balance</div>
            <div className="font-display text-2xl font-bold tabular-nums">{formatCurrency(adj_bank)}</div>
          </div>
        </Section>

        <Section title="B · Book Balance (DESCON)">
          <Row label="Balance per books" k="book_balance" />
          <Row label="Interest income not recorded" k="interest_income" sign="+" />
          <Row label="Bank charges not recorded" k="bank_charges" sign="−" />
          <Row label="NSF items" k="nsf_items" sign="−" />
          <div className="rounded-md bg-[#0f1e42] p-3 text-white">
            <div className="overline text-white/60">Adjusted book balance</div>
            <div className="font-display text-2xl font-bold tabular-nums">{formatCurrency(adj_book)}</div>
          </div>
        </Section>
      </div>

      <div className={`dc-card p-5 ${reconciled ? "border-emerald-300 bg-emerald-50/40" : "border-red-300 bg-red-50/40"}`} data-testid="recon-result">
        <div className="overline">C · Result</div>
        <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
          <div><div className="text-slate-500">Adjusted bank</div><div className="font-display text-lg font-bold tabular-nums">{formatCurrency(adj_bank)}</div></div>
          <div><div className="text-slate-500">Adjusted book</div><div className="font-display text-lg font-bold tabular-nums">{formatCurrency(adj_book)}</div></div>
          <div><div className="text-slate-500">Difference</div><div className={`font-display text-lg font-bold tabular-nums ${reconciled ? "text-emerald-700" : "text-red-700"}`}>{formatCurrency(diff)}</div></div>
        </div>
        <div className={`mt-3 text-sm font-medium ${reconciled ? "text-emerald-700" : "text-red-700"}`}>
          {reconciled ? "Reconciled — ready to submit." : "Discrepancy — review required. Submit is locked until the difference is zero."}
        </div>
      </div>

      <div>
        <Label>Notes</Label>
        <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={locked || !canEdit} />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {!locked && canEdit && <Button onClick={save} variant="outline" data-testid="recon-save-btn">Save</Button>}
        {!locked && canEdit && form.id && <Button onClick={submit} disabled={!reconciled} className="btn-primary-blue" data-testid="recon-submit-btn"><CloudArrowUp size={14} className="mr-1" /> Submit for CEO Approval</Button>}
        {form.status === "awaiting_approval" && isCeo && <Button onClick={approve} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="recon-approve-btn"><CheckCircle size={14} className="mr-1" /> Approve & lock</Button>}
        {locked && <span className="inline-flex items-center gap-1 text-emerald-700 text-sm font-medium"><Lock size={14} /> Locked</span>}
      </div>

      {history.length > 0 && (
        <div className="dc-card overflow-hidden">
          <div className="border-b border-slate-100 px-5 py-2 overline">Reconciliation history · {BANK_OPTIONS.find((b) => b.k === bankKey)?.label}</div>
          <div className="responsive-table-wrap">
            <table className="data-table w-full min-w-[700px]">
              <thead><tr><th>Period</th><th className="text-right">Bank</th><th className="text-right">Book</th><th className="text-right">Diff</th><th>Status</th></tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className={h.period === period ? "bg-blue-50/40" : ""}>
                    <td className="px-5 py-2 font-mono-alt text-xs">{h.period}</td>
                    <td className="px-5 py-2 text-right font-mono-alt tabular-nums">{formatCurrency(h.adjusted_bank_balance || 0)}</td>
                    <td className="px-5 py-2 text-right font-mono-alt tabular-nums">{formatCurrency(h.adjusted_book_balance || 0)}</td>
                    <td className={`px-5 py-2 text-right font-mono-alt tabular-nums ${Math.abs(h.difference || 0) <= 0.01 ? "text-emerald-700" : "text-red-700"}`}>{formatCurrency(h.difference || 0)}</td>
                    <td className="px-5 py-2"><StatusBadge status={h.status === "approved" ? "approved" : h.status === "awaiting_approval" ? "pending_review" : Math.abs(h.difference || 0) <= 0.01 ? "active" : "expiring_soon"} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Section 10 — Donor Reporting (rich)
// ══════════════════════════════════════════════════════════════
const CHECKLIST_LABELS = {
  signed_financial_statement: "Signed financial statement",
  bank_statement_for_period: "Bank statement for period",
  invoice_copies: "Invoice copies",
  procurement_log: "Procurement log",
  beneficiary_data: "Beneficiary data",
  delivery_proof: "Delivery & completion proof",
  narrative_report: "Narrative progress report",
  tax_compliance_certificate: "Tax compliance certificate",
  annual_audit_report: "Annual audit report",
  meeting_minutes: "Meeting minutes",
  supporting_schedules: "Supporting schedules",
  donor_specific_attachment: "Donor-specific attachment",
};
const STATUS_OPTIONS = [
  { v: "missing",     l: "Missing" },
  { v: "in_progress", l: "In progress" },
  { v: "complete",    l: "Complete" },
  { v: "na",          l: "Not applicable" },
];
const STATUS_TONE = {
  missing: "bg-red-100 text-red-700",
  in_progress: "bg-amber-100 text-amber-700",
  complete: "bg-emerald-100 text-emerald-700",
  na: "bg-slate-100 text-slate-600",
};
const PERIOD_LABEL = {
  monthly: "Monthly", quarterly: "Quarterly",
  semi_annual: "Semi-annual", annual: "Annual", custom: "Custom range",
};

export function DonorTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [submitDialog, setSubmitDialog] = useState(null);

  const load = async () => {
    const { data } = await api.get("/finance/donors");
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const update = async (donor, patch) => {
    try {
      await api.post("/finance/donors", { ...donor, ...patch });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setChecklistEntry = (d, key, patch) => {
    const prev = d.checklist?.[key];
    const existing = (prev && typeof prev === "object") ? prev
      : { included: !!prev, status: prev ? "complete" : "missing", ref: "", notes: "" };
    const next = { ...(d.checklist || {}), [key]: { ...existing, ...patch } };
    update(d, { checklist: next });
  };

  const statusFor = (d) => {
    if (d.status === "submitted" || d.status === "accepted") return "approved";
    if (d.days_remaining != null && d.days_remaining < 0) return "expired";
    if (d.days_remaining != null && d.days_remaining <= 14) return "expiring_soon";
    return "pending";
  };

  const downloadPackage = async (d) => {
    try {
      const res = await api.get(`/finance/donors/${d.donor_key}/package.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `donor-package-${d.donor_key}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const inferPeriod = (type) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    let s, e, label;
    if (type === "monthly") {
      const d1 = new Date(y, m, 1);
      const d2 = new Date(y, m + 1, 0);
      s = d1.toISOString().slice(0, 10); e = d2.toISOString().slice(0, 10);
      label = d1.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    } else if (type === "quarterly") {
      const q = Math.floor(m / 3);
      s = new Date(y, q * 3, 1).toISOString().slice(0, 10);
      e = new Date(y, q * 3 + 3, 0).toISOString().slice(0, 10);
      label = `Q${q + 1} ${y}`;
    } else if (type === "semi_annual") {
      const h = m < 6 ? 0 : 1;
      s = new Date(y, h * 6, 1).toISOString().slice(0, 10);
      e = new Date(y, h * 6 + 6, 0).toISOString().slice(0, 10);
      label = `${h === 0 ? "H1" : "H2"} ${y}`;
    } else if (type === "annual") {
      s = `${y}-01-01`; e = `${y}-12-31`; label = `FY${y}`;
    } else {
      s = ""; e = ""; label = "";
    }
    return { period_type: type, period_start: s, period_end: e, period_label: label };
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {items.map((d) => {
          const cl = d.checklist || {};
          const items_list = d.checklist_items || Object.keys(CHECKLIST_LABELS);
          const complete = items_list.filter((k) => {
            const v = cl[k];
            return v === true || (v && typeof v === "object" && v.status === "complete");
          }).length;
          const total = items_list.length;
          const pct = d.package_complete_pct ?? Math.round((complete / total) * 100);
          return (
            <details key={d.donor_key} className="dc-card overflow-hidden" open data-testid={`donor-panel-${d.donor_key}`}>
              <summary className="cursor-pointer list-none border-b border-slate-100 px-5 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-base font-semibold text-slate-900 truncate">
                      {d.donor_name || d.name || d.donor_key}
                    </div>
                    <div className="text-xs text-slate-500">
                      {PERIOD_LABEL[d.period_type] || "—"}
                      {d.period_label ? ` · ${d.period_label}` : ""}
                      {d.period_start && d.period_end ? ` · ${d.period_start} → ${d.period_end}` : ""}
                    </div>
                  </div>
                  <div className="text-right">
                    <StatusBadge status={statusFor(d)} />
                    <div className="mt-1 text-[11px] text-slate-500">
                      due {formatDate(d.next_due)}
                      {d.days_remaining != null && (
                        <> · {d.days_remaining < 0 ? `${Math.abs(d.days_remaining)}d overdue` : `${d.days_remaining}d remaining`}</>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <div className="flex items-center justify-between text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">
                    <span>Package completeness</span>
                    <span className="tabular-nums">{pct}%</span>
                  </div>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct === 100 ? "#10b981" : "#1E5CBA" }} />
                  </div>
                </div>
              </summary>
              <div className="space-y-4 p-5">
                {d.ar_outstanding > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid={`donor-ar-warning-${d.donor_key}`}>
                    <div className="font-semibold">Outstanding receivables linked to this donor</div>
                    <div>{d.ar_count} unpaid invoice(s) · {formatCurrency(d.ar_outstanding)} — flag in narrative.</div>
                  </div>
                )}

                {/* Reporting period selector */}
                {canEdit && (
                  <div className="rounded-md border border-slate-200 p-3">
                    <div className="overline mb-2">Reporting period</div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                      <div>
                        <Label>Type</Label>
                        <Select
                          value={d.period_type || "quarterly"}
                          onValueChange={(v) => update(d, inferPeriod(v))}
                        >
                          <SelectTrigger data-testid={`donor-period-type-${d.donor_key}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(PERIOD_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Label</Label>
                        <Input value={d.period_label || ""} onChange={(e) => update(d, { period_label: e.target.value })} placeholder="e.g. Q1 2026" data-testid={`donor-period-label-${d.donor_key}`} />
                      </div>
                      <div>
                        <Label>From</Label>
                        <Input type="date" value={d.period_start || ""} onChange={(e) => update(d, { period_start: e.target.value })} />
                      </div>
                      <div>
                        <Label>To</Label>
                        <Input type="date" value={d.period_end || ""} onChange={(e) => update(d, { period_end: e.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3 mt-3">
                      <div>
                        <Label>Next submission due</Label>
                        <Input type="date" value={d.next_due || ""} onChange={(e) => update(d, { next_due: e.target.value })} />
                      </div>
                      <div>
                        <Label>Reporting amount (USD)</Label>
                        <Input type="number" value={d.reporting_amount || ""} onChange={(e) => update(d, { reporting_amount: parseFloat(e.target.value || 0) })} />
                      </div>
                      <div>
                        <Label>Currency</Label>
                        <Input value={d.currency || "USD"} onChange={(e) => update(d, { currency: e.target.value })} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Structured document checklist */}
                <div>
                  <Label className="text-xs">Document package · {complete}/{total} complete</Label>
                  <ul className="mt-2 space-y-1.5">
                    {items_list.map((k) => {
                      const raw = cl[k];
                      const entry = (raw && typeof raw === "object") ? raw
                        : { included: !!raw, status: raw ? "complete" : "missing", ref: "", notes: "" };
                      return (
                        <li key={k} className="grid grid-cols-1 gap-2 rounded-md border border-slate-100 bg-slate-50/50 p-2 md:grid-cols-[20px_1fr_130px_150px_1fr]" data-testid={`donor-doc-${d.donor_key}-${k}`}>
                          <Checkbox
                            checked={!!entry.included}
                            onCheckedChange={(v) => canEdit && setChecklistEntry(d, k, { included: !!v, status: v ? (entry.status === "missing" ? "in_progress" : entry.status) : "missing" })}
                            disabled={!canEdit}
                            className="mt-1"
                          />
                          <div>
                            <div className="text-sm font-medium text-slate-900">{CHECKLIST_LABELS[k] || k}</div>
                            {entry.ref && <div className="text-[10px] font-mono-alt text-slate-500">ref: {entry.ref}</div>}
                          </div>
                          <Select
                            value={entry.status || "missing"}
                            onValueChange={(v) => canEdit && setChecklistEntry(d, k, { status: v, included: v !== "missing" && v !== "na" ? true : entry.included })}
                            disabled={!canEdit}
                          >
                            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Input
                            placeholder="Reference #"
                            className="h-8"
                            value={entry.ref || ""}
                            onChange={(e) => canEdit && setChecklistEntry(d, k, { ref: e.target.value })}
                            disabled={!canEdit}
                          />
                          <Input
                            placeholder="Notes"
                            className="h-8"
                            value={entry.notes || ""}
                            onChange={(e) => canEdit && setChecklistEntry(d, k, { notes: e.target.value })}
                            disabled={!canEdit}
                          />
                          <span className={`hidden md:inline-block col-start-3 row-start-1 mt-1 rounded px-1.5 py-0.5 text-center font-mono-alt text-[9px] uppercase tracking-widest ${STATUS_TONE[entry.status || "missing"]}`}>
                            {entry.status || "missing"}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
                  <Button variant="outline" size="sm" onClick={() => downloadPackage(d)} data-testid={`donor-download-${d.donor_key}`}>
                    <DownloadSimple size={12} className="mr-1" /> Download package (PDF)
                  </Button>
                  {canEdit && (
                    <Button size="sm" onClick={() => setSubmitDialog(d)} disabled={pct < 100} data-testid={`donor-submit-${d.donor_key}`} className="btn-primary-blue">
                      <Check size={12} className="mr-1" /> Mark as submitted
                    </Button>
                  )}
                </div>
              </div>
            </details>
          );
        })}
      </div>
      <DonorSubmitDialog donor={submitDialog} onClose={() => setSubmitDialog(null)} onDone={load} />
    </div>
  );
}

function DonorSubmitDialog({ donor, onClose, onDone }) {
  const [form, setForm] = useState({ submitted_to: "", method: "email", reference: "" });
  useEffect(() => { if (donor) setForm({ submitted_to: "", method: "email", reference: "" }); }, [donor]);
  if (!donor) return null;
  const submit = async () => {
    if (!form.submitted_to.trim() || !form.reference.trim()) return toast.error("Submitted-to and reference are required");
    try {
      await api.post(`/finance/donors/${donor.donor_key}/submit`, null, { params: form });
      toast.success("Logged as submitted"); onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={!!donor} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Mark report as submitted</DialogTitle>
          <DialogDescription>{donor.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Submitted to *</Label><Input value={form.submitted_to} onChange={(e) => setForm({ ...form, submitted_to: e.target.value })} placeholder="Contact name / email" /></div>
          <div><Label>Method</Label>
            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="portal">Donor portal</SelectItem>
                <SelectItem value="in_person">In person</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Reference *</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Confirmation / portal ref" /></div>
        </div>
        <DialogFooter><Button onClick={submit} className="btn-primary-blue">Confirm submission</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function CashflowTab({ canEdit }) {
  return (
    <div className="space-y-5">
      <CashflowScenarios />
      <CashflowTabBase canEdit={canEdit} />
    </div>
  );
}
